import { useId, useState } from 'react';
import { Box, Button } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';

/** Snippets longer than this are shortened until the reader asks for more. */
export const SNIPPET_PREVIEW_CHARS = 240;

/**
 * The failing code for one finding, compact enough for a table row (REV-713).
 *
 * The finding detail page renders the same markup through CodeBlock, which
 * pretty-prints with Prettier. That is too heavy to run once per row, so this
 * shows the capture as-is. It wraps, following CodeBlock's REV-544 reasoning:
 * a wrapped block never scrolls sideways, so there is no scroll container a
 * keyboard user would need to reach.
 *
 * Long captures are shortened, and expand in place. That matters most for an
 * unregistered scan, whose rows lead to no detail page, so this is the only
 * place its code can be read. The toggle stops propagation because the row
 * itself navigates.
 */
export default function FailingCode({ html }: { html: string }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const codeId = useId();
  const long = html.length > SNIPPET_PREVIEW_CHARS;
  const shown = long && !expanded ? `${html.slice(0, SNIPPET_PREVIEW_CHARS)}…` : html;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Box component="span" sx={visuallyHidden}>
        {t('findings.failingCode', 'Failing code')}:{' '}
      </Box>
      <Box
        component="code"
        id={codeId}
        sx={{
          display: 'block',
          fontFamily: 'monospace',
          fontSize: '0.75rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          bgcolor: 'action.hover',
          borderRadius: 0.5,
          px: 0.75,
          py: 0.5,
        }}
      >
        {shown}
      </Box>
      {long && (
        <Button
          size="small"
          aria-expanded={expanded}
          aria-controls={codeId}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          sx={{ mt: 0.25, px: 0.5, minWidth: 0, textTransform: 'none' }}
        >
          {expanded
            ? t('findings.showLessCode', 'Show less code')
            : t('findings.showFullCode', 'Show full code')}
        </Button>
      )}
    </Box>
  );
}
